import { useLocation } from 'react-router';

import { Translate } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { InlineMessage } from '@change-corgi/design-system-legacy/components/alerts';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { createModalHook } from '@change-corgi/design-system-legacy/modals';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { isError, isLoading } from 'src/app/shared/utils/async';

import { REOPEN_PETITION_ERROR_MAP } from './constants';
import { useReopenPetition } from './useReopenPetition';

type ClosePetitionProps = { petitionId: string; trackingData: { signatureCount: number } };
type ClosePetitionResult = { status: 'ok' | 'cancelled' };
export const useReopenPetitionModal = createModalHook<ClosePetitionProps, ClosePetitionResult>({
	name: 'ClosePetitionModal',
	title: () => (
		<Translate value="corgi.petition.starter_dashboard.petition_status.reopen_petition.reopen_confirmation_modal.title" />
	),
	body: function ClosePetitionModalBody({ closeModal, options: { petitionId, trackingData } }) {
		const navigate = useNavigate();
		const { pathname, search } = useLocation();
		const track = useTracking();
		const {
			mutate: reopenPetition,
			reset,
			...reopenPetitionState
		} = useReopenPetition(petitionId, {
			onSuccess: () => {
				void track('reopen_petition_success', {
					petition_id: petitionId,
					signature_count: trackingData.signatureCount,
				});
				closeModal({ status: 'ok' });
			},
			onError: (error) => {
				void track('reopen_petition_error', {
					petition_id: petitionId,
					signature_count: trackingData.signatureCount,
				});
				if (error === REOPEN_PETITION_ERROR_MAP.UNAUTHENTICATED_ERROR) {
					const redirectUrl = encodeURIComponent(pathname + search);
					navigate(`/login_or_join?redirectTo=${redirectUrl}`);
				}
			},
		});

		const handleClosePetition = async () => {
			void track('reopen_petition_modal_engaged', {
				petition_id: petitionId,
				signature_count: trackingData.signatureCount,
			});
			await reopenPetition();
		};

		const hasUnknownError =
			isError(reopenPetitionState) && reopenPetitionState.error !== REOPEN_PETITION_ERROR_MAP.UNAUTHENTICATED_ERROR;

		return (
			<Box>
				<Text>
					<Translate value="corgi.petition.starter_dashboard.petition_status.reopen_petition.reopen_confirmation_modal.paragraph_1" />
				</Text>
				{hasUnknownError && (
					<InlineMessage variant="error" mt={8} data-testid="reopen-error">
						<Translate value="corgi.petition.starter_dashboard.petition_status.reopen_petition.reopen_confirmation_modal.reopen_error_message" />
					</InlineMessage>
				)}
				<Flex mt={24} sx={{ gap: 16 }}>
					<Button
						data-qa="reopen-petition-cancel"
						variant="secondary"
						onClick={() => closeModal({ status: 'cancelled' })}
					>
						<Translate value="corgi.petition.starter_dashboard.petition_status.reopen_petition.reopen_confirmation_modal.cancel_button" />
					</Button>
					<Button
						data-qa="reopen-petition-confirm"
						onClick={handleClosePetition}
						loading={isLoading(reopenPetitionState)}
						sx={{
							backgroundColor: '#fbdb00',
							borderColor: '#fbdb00',
							'& > span': { color: '#000' },
							'&:hover': { filter: 'brightness(1)' },
						}}
					>
						<Translate value="corgi.petition.starter_dashboard.petition_status.reopen_petition.reopen_confirmation_modal.reopen_petition_button" />
					</Button>
				</Flex>
			</Box>
		);
	},
});
